/**
 * The thread.
 *
 * ## Why the container is `flex-col-reverse`
 *
 * A reversed flex container plus a `flex-1` spacer pins the newest message to the
 * bottom and keeps it pinned as content grows — no scroll-anchoring effect, no
 * `scrollTop = scrollHeight` on every render, and prepending an older page does not
 * yank the viewport.
 *
 * The container reverses only its DIRECT children, so day groups are emitted
 * newest-first (the first child sits at the bottom) while the messages INSIDE a group —
 * a plain block element — stay oldest-first and read top-to-bottom as written.
 *
 * ## The scroller is a `BlockScroll`, and it reserves its bar's width
 *
 * The thread owns its scrolling: `BlockScroll` is the house block-scroll container, and
 * `overscroll-none` on top of it stops a thread scrolled to its end from carrying the
 * gesture on to the page behind it. Only the reversed layout gets it — `classic` is a
 * natural-height list that scrolls its PAGE, so making it a scroll container would give
 * the member order thread a second scrollbar inside the card.
 *
 * It is passed `reversed`, which is not cosmetic. `BlockScroll`'s fade rides a scroll
 * timeline, and a timeline measures from the scroll ORIGIN — which in a `column-reverse`
 * scrollport is the BOTTOM. Left forward, the mask softens whichever edge has nothing
 * behind it: resting on the newest message it fades the bottom and leaves the top crisp
 * although the thread continues above. The flag swaps the two back.
 *
 * The bar is the browser's own. `scrollbar-gutter-stable` is what keeps it from moving
 * anything: a classic bar (Windows, Linux, macOS set to "always") is laid out INSIDE the
 * scroller, so without the reservation the messages are one scrollbar narrower exactly
 * while the thread happens to be long enough to scroll, and jump back out when it is not.
 * Reserved, the content box is the same width either way.
 *
 * That still leaves the composer below, which is not a scroll container and so reserves
 * nothing — it would be one scrollbar WIDER than the messages. `scrollbar-gutter-match`
 * on the composer's own box is the other half of the pair; see `channel-view.tsx`.
 *
 * `flex-col-reverse` has to stay on the SCROLL CONTAINER itself: the browser's bottom-pinning
 * is a property of the scrollport's own direction, and `scrollChannelToBottom` scrolls to a
 * large positive `scrollTop` precisely because the newest message is this container's maximum.
 *
 * ## Where the cap belongs
 *
 * A surface caps the band it wants with `surface.contentClassName`, which lands on the
 * content column INSIDE the scroller and on the composer's column below it. The scroller
 * itself stays the panel's full width, so the bar is drawn at the panel's own edge and the
 * band keeps its whole width instead of losing a scrollbar's worth of it — the shape the
 * order detail tabs use (`features/office/orders/detail/main/index.tsx`), followed by the
 * office inbox thread (`features/office/inbox/_components/inbox-thread-panel.tsx`) and
 * Member Chats (`features/my/chats/index.tsx`). The gutter pair above is what holds the
 * two capped columns on one axis: both centre inside a box that reserves the same bar.
 *
 * Capping by wrapping the whole view still works — the scroller is then the capped column
 * and both boxes agree at the narrower width — but it drags the bar in off the panel's
 * edge and spends a scrollbar of the band on it.
 *
 * ## Why "load older" is a sentinel, and why it needs no layout branch
 *
 * The last DOM child is the OLD end of the thread in both layouts: reversed, it renders
 * at the visual top; `classic` (newest-first, natural height) puts it at the visual
 * bottom. So one observed sentinel in one position serves both — see
 * `useLoadOlderOnVisible` for why it is observed rather than scroll-listened.
 */

import { BlockScroll } from "@birdwatch/ui";
import { cn } from "cn";
import { isEmpty } from "lodash-es";
import { type ReactNode, type RefObject, useEffect, useRef } from "react";
import { LineAndDate } from "@/components/ui/common/line-and-date";
import { MessageEmpty } from "@/components/ui/message";
import type {
  Channel,
  ChannelMessage,
  ChannelQuotedMessage,
} from "../_api/channel-endpoints";
import { useLoadOlderOnVisible } from "../_hooks/use-load-older-on-visible";
import { useNewMessageDivider } from "../_hooks/use-new-message-divider";
import { groupMessagesByDay } from "../_libs/group-messages-by-day";
import { showsReadReceipt } from "../_libs/read-receipt-visibility";
import { isMessageUnread, type ReadCursorId } from "../_libs/unread-boundary";
import type {
  ChannelDaySeparatorRenderer,
  ChannelMessageRenderer,
  ChannelNewDividerRenderer,
  ResolvedChannelSurface,
} from "../_types/channel-surface";
import { ChannelMessageEdit } from "./channel-message-edit";
import { ChannelMessageItem } from "./channel-message-item";
import {
  ChannelMessageSkeleton,
  ChannelMessageSkeletonLoadMore,
} from "./channel-message-skeleton";

export type ChannelMessageListProps = {
  cid: string;
  channel: Channel;
  /** Oldest-first. */
  messages: ChannelMessage[];
  containerRef: RefObject<HTMLDivElement | null>;
  timeZone: string;
  viewerId: string | undefined;
  /**
   * The viewer's global roles — only the OTHER side of the thread carries a dot.
   * `undefined` until `auth/me` answers, which withholds every dot until it does.
   */
  viewerRoles: readonly string[] | undefined;
  surface: ResolvedChannelSurface;
  isLoading: boolean;
  isFetchingNextPage: boolean;
  /**
   * Backwards pagination. Both omitted means this list does not page at all (the comment
   * thread, whose replies are capped at one page) and no sentinel is rendered.
   */
  hasNextPage?: boolean;
  fetchNextPage?: () => void;
  /** The viewer's live read cursor; the fallback when the channel has no audience. */
  lastReadMessageId?: ReadCursorId;
  /**
   * Int `Message.id`s the viewer's SIDE still has unread. Authoritative when present —
   * see rule 2b in `_libs/unread-boundary.ts`; `undefined` means this channel type has
   * no audience and the cursor decides.
   */
  audienceUnreadIds?: readonly string[];
  /** Int `Message.id` to scroll to and flash. */
  focusedMessageId: string | null;
  /**
   * Bumped each time the SAME `focusedMessageId` is asked for again. Two clicks on one
   * search result must scroll and flash twice: without this the scroll is a no-op (the
   * effect below remembers where it landed) and the flash never restarts (its class
   * never leaves the node).
   */
  focusNonce?: number;
  /** Int `Message.id` currently open in the inline editor. */
  editingMessageId: string | null;
  onCancelEdit: () => void;
  onEditMessage?: (message: ChannelMessage) => void;
  /** The quote the open editor holds — the editing message's, until Reply or ✕ changes it. */
  editingQuote: ChannelQuotedMessage | null;
  onEditingQuoteChange: (quote: ChannelQuotedMessage | null) => void;
  onDeleteMessage?: (message: ChannelMessage) => void;
  onTogglePin?: (message: ChannelMessage) => void;
  /** Quote a message in the composer ("Quote reply"). */
  onReplyMessage?: (message: ChannelMessage) => void;
  /** Scroll to the message a reply quotes. */
  onOpenQuotedMessage?: (messageId: string) => void;
  /**
   * Pinned above everything (the thread view's root message). Emitted LAST because the
   * container is reversed — last child, top of the viewport.
   */
  topSlot?: ReactNode;
  /**
   * Swaps the bubble for another UI. Omitted, the list renders `ChannelMessageItem` —
   * so every surface that does not opt in behaves exactly as it did before this seam existed.
   */
  renderMessage?: ChannelMessageRenderer;
  /** Swaps the day-group heading. Omitted, the list renders `LineAndDate`. */
  renderDaySeparator?: ChannelDaySeparatorRenderer;
  /**
   * Draws the "New" marker at the unread boundary. Omitted, no marker is drawn — the
   * surface opts in by supplying the chrome.
   */
  renderNewDivider?: ChannelNewDividerRenderer;
  /**
   * `GET /:cid/unread` has answered for this cid and is not revalidating. The marker
   * waits for it because the query client serves the PREVIOUS visit's read state on
   * mount — see `_libs/new-message-divider`.
   */
  unreadSettled?: boolean;
};

export function ChannelMessageList({
  cid,
  channel,
  messages,
  containerRef,
  timeZone,
  viewerId,
  viewerRoles,
  surface,
  isLoading,
  isFetchingNextPage,
  hasNextPage,
  fetchNextPage,
  lastReadMessageId,
  audienceUnreadIds,
  focusedMessageId,
  focusNonce = 0,
  editingMessageId,
  onCancelEdit,
  onEditMessage,
  editingQuote,
  onEditingQuoteChange,
  onDeleteMessage,
  onTogglePin,
  onReplyMessage,
  onOpenQuotedMessage,
  topSlot,
  renderMessage,
  renderDaySeparator,
  renderNewDivider,
  unreadSettled = false,
}: ChannelMessageListProps) {
  useScrollToMessage(
    containerRef,
    focusedMessageId,
    focusNonce,
    messages.length,
  );

  const paginates = !!hasNextPage && fetchNextPage != null;
  const { sentinelRef } = useLoadOlderOnVisible({
    fetchNextPage: fetchNextPage ?? noop,
    hasNextPage: paginates,
    isFetchingNextPage,
  });

  // `"classic"` (the member order thread) flows newest-first top-to-bottom in natural
  // height; the default `"chat"` layout is a reversed scroller that bottom-pins newest.
  const classic = surface.layout === "classic";
  const MessageItem = renderMessage ?? ChannelMessageItem;
  const DaySeparator = renderDaySeparator ?? LineAndDate;
  const NewDivider = renderNewDivider;

  // Latched for the visit, unlike the per-message dots beside it — see the hook.
  const newDividerMessageId = useNewMessageDivider(
    cid,
    messages,
    { lastReadMessageId, viewerId, viewerRoles, audienceUnreadIds },
    unreadSettled,
    surface.unreadDots && NewDivider != null,
  );
  const dayGroups = groupMessagesByDay(messages).reverse();
  const isEmptyThread = isEmpty(messages) && !isLoading;

  /* The content column. It is a SEPARATE box from the scroller below it so the
     scrollbar can sit at the panel's own edge while the messages stay capped and
     centred — the shape `ContainerWrapper` gives the order detail tabs
     (`features/office/orders/detail/main/index.tsx`).

     `flex-1`, NOT `min-h-full`: the scroller is a FLEX container, so `min-height`
     here would replace this item's `min-height: auto` — the automatic minimum size
     that stops `flex-shrink` from crushing it. A thread taller than the viewport
     would then shrink to 100% and spill, and the empty state's `h-full` below would
     stop resolving. `w-full` is load-bearing too: `mx-auto` turns off
     `align-self: stretch`, so without a definite width this column would
     shrink-to-fit its content. */
  const content = (
    <div
      className={cn(
        classic
          ? "flex flex-col"
          : "flex flex-col-reverse flex-1 w-full mx-auto",
        surface.contentClassName,
      )}
    >
      {isLoading ? (
        <ChannelMessageSkeleton />
      ) : (
        <>
          {!isEmptyThread && !classic && <div className="flex-1" />}
          {isEmptyThread && (
            <div
              // With a `topSlot` (or the natural-height classic layout) the empty state
              // must not claim the full height — `h-full` collapses without a bounded parent.
              className={
                classic
                  ? "flex items-center justify-center py-10"
                  : topSlot
                    ? "flex items-center justify-center py-10 h-full"
                    : "flex items-center justify-center h-full"
              }
            >
              {surface.emptyState ?? <MessageEmpty />}
            </div>
          )}
          {dayGroups.map((group) => (
            <div data-date={group.date} key={group.date}>
              {surface.daySeparators &&
                (classic ? (
                  <DaySeparator date={group.date} />
                ) : (
                  // `top-10`, not `top-0`: the scroller's fade is a mask, and a
                  // mask applies to everything painted inside the element — a chip stuck
                  // flush to the edge would sit in the fade band and wash out. Parking it
                  // one fade-height down (`--scroll-fade-size`'s default) keeps it opaque.
                  <div className="sticky top-5 z-20">
                    <DaySeparator date={group.date} />
                  </div>
                ))}
              {(classic
                ? group.messages.slice().reverse()
                : group.messages
              ).map((message) => (
                <div key={message.id}>
                  {/* `classic` runs newest-first, so the unread block sits ABOVE the
                      boundary and the marker follows its message instead of preceding it. */}
                  {NewDivider &&
                    message.id === newDividerMessageId &&
                    !classic && <NewDivider />}
                  {editingMessageId === message.id ? (
                    <ChannelMessageEdit
                      cid={cid}
                      message={message}
                      onDone={onCancelEdit}
                      surface={surface}
                      quote={editingQuote}
                      onQuoteChange={onEditingQuoteChange}
                    />
                  ) : (
                    <MessageItem
                      // Remounting ONLY the focused bubble restarts `animate-highlight`
                      // (a 5s one-shot that will not replay while its class stays on the
                      // node), so a repeat jump flashes again. Every other row keeps its
                      // plain id and never remounts.
                      key={
                        focusedMessageId === message.id
                          ? `${message.id}:${focusNonce}`
                          : message.id
                      }
                      cid={cid}
                      channel={channel}
                      message={message}
                      viewerId={viewerId}
                      isUnread={
                        surface.unreadDots &&
                        isMessageUnread(message, {
                          lastReadMessageId,
                          viewerId,
                          viewerRoles,
                          audienceUnreadIds,
                        })
                      }
                      isHighlighted={focusedMessageId === message.id}
                      showReadReceipts={showsReadReceipt(
                        surface.readReceipts,
                        message.userCreatedId,
                        viewerId,
                      )}
                      showPinnedBadge={surface.pins}
                      actionsConfig={surface.messageActions}
                      renderBlock={surface.renderBlock}
                      renderAvatar={surface.renderAvatar}
                      renderUserName={surface.renderUserName}
                      onEdit={onEditMessage}
                      onDelete={onDeleteMessage}
                      onTogglePin={onTogglePin}
                      onReply={onReplyMessage}
                      onOpenQuotedMessage={onOpenQuotedMessage}
                    />
                  )}
                  {NewDivider &&
                    message.id === newDividerMessageId &&
                    classic && <NewDivider />}
                </div>
              ))}
            </div>
          ))}
          {isFetchingNextPage && <ChannelMessageSkeletonLoadMore />}
          {/* `shrink-0`: a zero-basis child in a flex column collapses and never intersects. */}
          {paginates && (
            <div ref={sentinelRef} aria-hidden className="h-px shrink-0" />
          )}
          {topSlot}
        </>
      )}
    </div>
  );

  // `classic` is deliberately NOT a scroll container — it is a natural-height list that
  // scrolls its PAGE, so wrapping it would give the member order thread a second scrollbar
  // inside its card. Only the reversed chat layout owns its scrolling, and `BlockScroll`
  // hard-codes `overflow-y-auto`, so the two branches render different elements around the
  // one shared column above.
  const scroller = classic ? (
    <div
      data-test="channel-message-list"
      ref={containerRef}
      className={cn("flex flex-col", surface.listClassName)}
    >
      {content}
    </div>
  ) : (
    <BlockScroll
      data-test="channel-message-list"
      ref={containerRef}
      // The scrollport is `flex-col-reverse`, which puts the scroll origin at the BOTTOM.
      // Without this the fade masks the edge that has nothing behind it at both ends.
      reversed
      className={cn(
        /* sorted */
        "relative flex flex-col-reverse flex-1 overscroll-none scrollbar-gutter-stable",
        surface.listClassName,
      )}
    >
      {content}
    </BlockScroll>
  );

  return scroller;
}

function noop() {}

/**
 * Brings a deep-linked, pinned-jump or search-jump message into view — once per REQUEST.
 *
 * The target need not be in the DOM when its id arrives: a link can name a message pages
 * back while `useResolvedMessageTarget` is still walking towards it. So the effect re-runs
 * whenever the loaded window changes, and retries until the node exists.
 *
 * What it must NOT do is scroll a second time once it has landed. That same window changes
 * two or three times per own send (optimistic insert, then the post-send refetch) and again
 * on every realtime arrival — a re-scroll there yanks the viewport off the comment the viewer
 * just posted, while fighting the composer's own `scrollChannelToBottom`. Remembering what it
 * actually scrolled to separates "still looking" from "done": a miss leaves the ref untouched,
 * so only a real hit ends the retries.
 *
 * What it remembers is the REQUEST (`nonce:id`), not the id. Clicking one search result twice
 * is two requests for one message — the second must scroll again — while every re-render in
 * between carries the same nonce and is correctly ignored.
 */
function useScrollToMessage(
  containerRef: RefObject<HTMLDivElement | null>,
  messageId: string | null,
  focusNonce: number,
  loadedCount: number,
) {
  const scrolledToRef = useRef<string | null>(null);

  useEffect(() => {
    if (messageId == null) return;

    const request = `${focusNonce}:${messageId}`;
    if (scrolledToRef.current === request) return;

    const container = containerRef.current;
    if (!container) return;

    const target = container.querySelector(`[data-message-id="${messageId}"]`);
    if (!target) return;

    scrolledToRef.current = request;
    target.scrollIntoView({ block: "center", behavior: "smooth" });
  }, [messageId, focusNonce, loadedCount]);
}
